import { useCallback, useState } from 'react';

// An element's rendered width, kept current with a ResizeObserver. Returns a CALLBACK ref (not a
// RefObject like useContainerSize) so it attaches whenever the element mounts, including one that
// only appears after data loads. Measured during commit, so the first reading lands before paint.
// A 0 reading (detached or hidden) keeps the last real width rather than reporting 0.
export const useElementWidth = <T extends HTMLElement>(): [
  (el: T | null) => (() => void) | undefined,
  number | undefined
] => {
  const [width, setWidth] = useState<number>();

  const ref = useCallback((el: T | null) => {
    if (!el) return undefined;
    const measure = () => {
      const next = el.getBoundingClientRect().width;
      if (next > 0) {
        setWidth(next);
      }
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};
